import { Buffer, Container, Geometry, Mesh, Point, Shader } from "pixi.js"
import { isAnyTreeEntityTile } from "game-common/map/map"
import { Coordinate, TileSize } from "game-common/models"
import { ScalingParams } from "../../../../client_util"

interface ForestTileSource {
    roomId: string
    twoStoreyHouse?: { groundRoomId?: string }
    width(): number
    height(): number
    getEntityTileAt(x: number, y: number): { entityTile?: string } | null | undefined
}

// Tree trunks within three tiles only. A continuous, squared radial weight avoids a tree
// suddenly changing the sight radius as the player crosses the edge of the sample circle.
export const sampleTreeDensity = (map: ForestTileSource, center: Coordinate): number => {
    const cx = center.x / TileSize
    const cy = center.y / TileSize
    let trees = 0
    let total = 0
    for (let x = Math.max(0, Math.floor(cx - 3)); x <= Math.min(map.width() - 1, Math.ceil(cx + 3)); x++) {
        for (let y = Math.max(0, Math.floor(cy - 3)); y <= Math.min(map.height() - 1, Math.ceil(cy + 3)); y++) {
            const distanceSquared = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2
            if (distanceSquared >= 9) continue
            const weight = (1 - distanceSquared / 9) ** 2
            total += weight
            // Same entity-tile convention as the Ember minimap; saplings are not canopy.
            if (isAnyTreeEntityTile(map.getEntityTileAt(x, y)?.entityTile)) trees += weight
        }
    }
    return total > 0 ? trees / total : 0
}

export const forestSightRadius = (density: number): number =>
    // Even the densest forest keeps half the open-ground sight radius.
    TileSize * (9 - 4.5 * Math.min(1, Math.max(0, density) / 0.35))

// About 4.5 seconds to settle through 95% of a density change, in either direction.
const DENSITY_TRANSITION_MS = 1500

// One quad, no blur pass or render texture. The shader computes a smooth radial fade all the
// way to opaque black. World-relative coordinates keep it centred through camera edge clamping.
// Console: window.forestVision = false disables the experiment without reloading.
export class ForestVision {
    readonly container = new Container()
    private mesh?: Mesh<Shader>
    private buffer?: Buffer
    private vertices = new Float32Array(8)
    private probe = new Point()
    private topLeft = new Point()
    private bottomRight = new Point()
    private roomId?: string
    private density = 0
    private lastMs = 0

    constructor(private surface: Container, private screen: { width: number; height: number }) {
        this.container.zIndex = -50
        // Overlay children supply their own world zoom, unlike the ground surface.
        this.container.scale.set(ScalingParams.surfaceZoom)
        this.container.interactiveChildren = false
        this.container.visible = false
    }

    update(enabled: boolean, map?: ForestTileSource, center?: Coordinate) {
        if (!enabled || !map || !center || (window as any).forestVision === false) {
            this.container.visible = false
            this.roomId = undefined
            return
        }
        const now = performance.now()
        const target = sampleTreeDensity(map, center)
        // A new place starts at its own density; a change of level in a house is the same place, and
        // eases there like any other step, or the veil snapped off and on with every stair.
        const sceneId = map.twoStoreyHouse?.groundRoomId ?? map.roomId
        if (this.roomId !== sceneId) {
            this.density = target
            this.roomId = sceneId
        } else {
            const blend = 1 - Math.exp(-Math.max(0, now - this.lastMs) / DENSITY_TRANSITION_MS)
            this.density += (target - this.density) * blend
        }
        this.lastMs = now
        if (target === 0 && this.density < 0.0001) this.density = 0
        this.container.visible = this.density > 0
        if (!this.container.visible) return

        const mesh = this.ensureMesh()
        this.probe.set(0, 0)
        this.surface.toLocal(this.probe, undefined, this.topLeft)
        this.probe.set(this.screen.width, this.screen.height)
        this.surface.toLocal(this.probe, undefined, this.bottomRight)
        this.topLeft.x /= ScalingParams.surfaceZoom
        this.topLeft.y /= ScalingParams.surfaceZoom
        this.bottomRight.x /= ScalingParams.surfaceZoom
        this.bottomRight.y /= ScalingParams.surfaceZoom
        const left = Math.min(this.topLeft.x, this.bottomRight.x) - TileSize - center.x
        const right = Math.max(this.topLeft.x, this.bottomRight.x) + TileSize - center.x
        const top = Math.min(this.topLeft.y, this.bottomRight.y) - TileSize - center.y
        const bottom = Math.max(this.topLeft.y, this.bottomRight.y) + TileSize - center.y
        this.vertices.set([left, top, right, top, right, bottom, left, bottom])
        this.buffer.update(this.vertices)
        mesh.position.set(center.x, center.y)
        mesh.shader.uniforms.uRadius = forestSightRadius(this.density)
        const strength = Math.min(1, this.density / 0.12)
        mesh.shader.uniforms.uStrength = strength * strength * (3 - 2 * strength)
    }

    private ensureMesh(): Mesh<Shader> {
        if (this.mesh) return this.mesh
        this.buffer = new Buffer(this.vertices, false, false)
        const geometry = new Geometry()
            .addAttribute("aVertexPosition", this.buffer, 2)
            .addIndex([0, 1, 2, 0, 2, 3])
        const shader = Shader.from(`
            precision highp float;
            attribute vec2 aVertexPosition;
            uniform mat3 projectionMatrix;
            uniform mat3 translationMatrix;
            varying vec2 vRelative;
            void main() {
                vRelative = aVertexPosition;
                gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
            }
        `, `
            precision mediump float;
            varying vec2 vRelative;
            uniform float uRadius;
            uniform float uStrength;
            void main() {
                float darkness = smoothstep(uRadius * 0.30, uRadius, length(vRelative));
                gl_FragColor = vec4(0.0, 0.0, 0.0, darkness * uStrength);
            }
        `, { uRadius: 9 * TileSize, uStrength: 0 })
        this.mesh = new Mesh(geometry, shader)
        this.container.addChild(this.mesh)
        return this.mesh
    }
}
